import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@eriko/ui/src/components/ui/dialog';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Label } from '@eriko/ui/src/components/ui/label';
import { Textarea } from '@eriko/ui/src/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useReconciliationStore } from '../../stores/reconciliation';
import { useCreateManualMatch } from '../../hooks/useReconciliation';
import { AllocationBuilder, type Allocation } from './AllocationBuilder';
import type { MatchedObjectType } from '@eriko/shared-types';

// TRANSFER is auto-detected by the engine's transfer stage (not manually creatable here);
// UNKNOWN is an engine-internal sentinel — never user-visible.
const TYPES: MatchedObjectType[] = [
  'BILL',
  'INVOICE',
  'RECEIPT',
  'EXPENSE',
  'PAYROLL',
  'TAX_PAYMENT',
  'PAYOUT',
];

interface Props {
  orgId: string;
}

export function ManualMatchDialog({ orgId }: Props) {
  const open = useReconciliationStore((s) => s.manualMatchOpen);
  const setOpen = useReconciliationStore((s) => s.setManualMatchOpen);
  const createMatch = useCreateManualMatch(orgId);

  const [transactionId, setTransactionId] = useState('');
  const [matchedObjectType, setMatchedObjectType] = useState<MatchedObjectType>('BILL');
  const [matchedObjectId, setMatchedObjectId] = useState('');
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [notes, setNotes] = useState('');
  // For payouts, allow either single-target (objectId) or many-to-one (allocations)
  const usingAllocations = matchedObjectType === 'PAYOUT';

  const reset = () => {
    setTransactionId('');
    setMatchedObjectType('BILL');
    setMatchedObjectId('');
    setAllocations([]);
    setNotes('');
  };

  const handleSubmit = async () => {
    if (!transactionId) return;
    if (usingAllocations) {
      if (allocations.length === 0) return;
      await createMatch.mutateAsync({
        transactionId,
        matchedObjectType: 'PAYOUT',
        allocations,
        notes: notes || undefined,
      });
    } else {
      if (!matchedObjectId) return;
      await createMatch.mutateAsync({
        transactionId,
        matchedObjectType,
        matchedObjectId,
        notes: notes || undefined,
      });
    }
    reset();
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        setOpen(o);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create manual match</DialogTitle>
          <DialogDescription>
            Attach a bill, invoice, document, or payout breakdown to a bank transaction.
            The match will be CONFIRMED immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="tx-id">Transaction id</Label>
            <Input
              id="tx-id"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              placeholder="tx_..."
            />
          </div>

          <div>
            <Label>Match type</Label>
            <Select
              value={matchedObjectType}
              onValueChange={(v) => setMatchedObjectType(v as MatchedObjectType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!usingAllocations ? (
            <div>
              <Label htmlFor="match-id">
                {matchedObjectType === 'BILL' || matchedObjectType === 'INVOICE'
                  ? 'AccountingEntity id'
                  : 'Document id'}
              </Label>
              <Input
                id="match-id"
                value={matchedObjectId}
                onChange={(e) => setMatchedObjectId(e.target.value)}
                placeholder="ent_... or doc_..."
              />
            </div>
          ) : (
            <AllocationBuilder
              allocations={allocations}
              onChange={setAllocations}
              targetAmount={0}
            />
          )}

          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              className="min-h-[60px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !transactionId ||
              createMatch.isPending ||
              (!usingAllocations && !matchedObjectId) ||
              (usingAllocations && allocations.length === 0)
            }
          >
            {createMatch.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating…
              </>
            ) : (
              'Create match'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
